import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import type {
  FertilizerAutoEvidenceItem,
  FieldPreparationAutoEvidenceItem,
  ReseedingAutoEvidenceItem,
} from "./autoEvidenceTypes";

const baseUrl = import.meta.env.VITE_API_ENDPOINT;

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
    "Content-Type": "application/json",
  };
}

function buildUrl(
  path: string,
  userGuid: string,
  actionSubmissionGuid?: string
): string {
  let url = `${baseUrl}/v2/autoEvidence/${path}?userGuid=${encodeURIComponent(
    userGuid
  )}`;
  if (actionSubmissionGuid) {
    url += `&actionSubmissionGuid=${encodeURIComponent(actionSubmissionGuid)}`;
  }
  return url;
}

async function fetchReseedingAutoEvidence(
  userGuid: string,
  actionSubmissionGuid?: string
): Promise<ReseedingAutoEvidenceItem[]> {
  const res = await fetch(
    buildUrl("reseeding", userGuid, actionSubmissionGuid),
    {
      headers: authHeaders(),
    }
  );
  if (!res.ok) return [];
  return (await res.json()) as ReseedingAutoEvidenceItem[];
}

async function fetchFertilizerAutoEvidence(
  userGuid: string,
  actionSubmissionGuid?: string
): Promise<FertilizerAutoEvidenceItem[]> {
  const res = await fetch(
    buildUrl("fertilizer", userGuid, actionSubmissionGuid),
    {
      headers: authHeaders(),
    }
  );
  if (!res.ok) return [];
  return (await res.json()) as FertilizerAutoEvidenceItem[];
}

async function fetchFieldPreparationAutoEvidence(
  userGuid: string,
  actionSubmissionGuid?: string
): Promise<FieldPreparationAutoEvidenceItem[]> {
  const res = await fetch(
    buildUrl("fieldPreparation", userGuid, actionSubmissionGuid),
    { headers: authHeaders() }
  );
  if (!res.ok) return [];
  return (await res.json()) as FieldPreparationAutoEvidenceItem[];
}

export function useReseedingAutoEvidence(
  userGuid: string | undefined,
  actionSubmissionGuid?: string
) {
  return useQuery({
    queryKey: queryKeys.autoEvidence.reseeding(userGuid ?? ""),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => fetchReseedingAutoEvidence(userGuid!, actionSubmissionGuid),
    enabled: !!userGuid,
  });
}

export function useFertilizerAutoEvidence(
  userGuid: string | undefined,
  actionSubmissionGuid?: string
) {
  return useQuery({
    queryKey: queryKeys.autoEvidence.fertilizer(userGuid ?? ""),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => fetchFertilizerAutoEvidence(userGuid!, actionSubmissionGuid),
    enabled: !!userGuid,
  });
}

export function useFieldPreparationAutoEvidence(
  userGuid: string | undefined,
  actionSubmissionGuid?: string
) {
  return useQuery({
    queryKey: queryKeys.autoEvidence.fieldPreparation(userGuid ?? ""),
    queryFn: () =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fetchFieldPreparationAutoEvidence(userGuid!, actionSubmissionGuid),
    enabled: !!userGuid,
  });
}
